/**
 * Shared portfolio data context — fetched once per org, shared across all pages.
 *
 * Eliminates redundant API fetches when navigating between pages.
 * Data is re-fetched only when the org changes or refresh() is called.
 */
import { createContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from '../components/Auth/AuthContext'
import { api } from '../lib/api'

export const PortfolioDataContext = createContext(null)

export function PortfolioDataProvider({ children }) {
  const { currentOrg } = useAuth()
  const orgId = currentOrg?.id

  const [data, setData] = useState(null)
  const [fundVariables, setFundVariables] = useState(null)
  const [fxData, setFxData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAll = useCallback(async () => {
    if (!orgId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    try {
      const [portfolioResp, varsResp, fxResp] = await Promise.all([
        api.get(`/portfolio/${orgId}`),
        api.get(`/funds/${orgId}/variables`),
        api.get(`/portfolio/${orgId}/fx`),
      ])

      const varsMap = {}
      for (const fv of varsResp) {
        const fi = fv.fund_investments
        const key = `${fi.funds.fund_name}||${fi.investors.investor_name}`
        const leveraged = fv.is_leveraged
        const participantPct = leveraged ? (fv.participant_pct || 15) : 100
        const calcTotalExposure = leveraged && participantPct > 0
          ? fi.total_commitment / (participantPct / 100)
          : fi.total_commitment

        varsMap[key] = {
          Fund: fi.funds.fund_name,
          Vehicle: fi.investors.investor_name,
          Currency: fi.funds.currency,
          'Leveraged?': leveraged ? 'Yes' : 'No',
          'My Commitment': fi.total_commitment,
          'Total Exposure': calcTotalExposure,
          'Leverage Ratio': leveraged ? fv.leverage_ratio : null,
          'Participant %': participantPct,
          'EQT Perf Loan %': leveraged ? fv.eqt_perf_loan_pct : 0,
          'SEB Loan %': leveraged ? fv.seb_loan_pct : 0,
          'EQT Perf Rate': leveraged ? fv.eqt_perf_rate : 0,
          'Avg Interest Rate (%)': leveraged ? fv.seb_base_rate : 0,
          'Target Fund MOIC': fv.target_fund_moic,
          'Vintage Year': fi.funds.vintage_year,
          'Maturity Year': fi.funds.maturity_year,
          'Payout Years': fv.payout_years,
          'Roth %': fv.roth_pct,
          'Traditional IRA %': fv.traditional_ira_pct,
          'Taxable %': fv.taxable_pct,
          'Co-Invest Amount': fv.co_invest_amount,
          'Holding Period': fv.holding_period,
          'First Investment Year': fv.first_investment_year,
          'Fund Area': fv.fund_areas?.name || null,
          'Fund Strategy': fv.fund_strategies?.name || null,
          'Product Type': fv.product_types?.name || null,
        }
      }

      const fxNormalized = {
        ...fxResp,
        current_rate: fxResp.current_rate ?? 1.0,
        fx_timeline: (fxResp.fx_timeline || []).map((p) => ({
          ...p,
          eurUsd: p.eurUsd ?? p.rate,
        })),
        fx_fund_impact: Array.isArray(fxResp.fx_fund_impact) ? fxResp.fx_fund_impact : [],
      }

      setData(portfolioResp)
      setFundVariables(varsMap)
      setFxData(fxNormalized)
    } catch (e) {
      setError(e.message)
      console.error('Failed to load portfolio data:', e)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    setData(null)
    setFundVariables(null)
    setFxData(null)
    fetchAll()
  }, [fetchAll])

  return (
    <PortfolioDataContext.Provider value={{ data, fundVariables, fxData, loading, error, refresh: fetchAll }}>
      {children}
    </PortfolioDataContext.Provider>
  )
}
